import { Building2, Home, Syringe, Stethoscope, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const REGION_FALLBACK = { flag: "🌍", color: "text-gray-500" };

function brandIcon(key, color) {
  switch (key) {
    case "blackswan":
      return <Building2 className="w-4 h-4" style={{ color }} />;
    case "blackswan_realestate":
      return <Home className="w-4 h-4" style={{ color }} />;
    case "prazi":
      return <Syringe className="w-4 h-4" style={{ color }} />;
    default:
      return <Stethoscope className="w-4 h-4" style={{ color }} />;
  }
}

export default function BrandRegionSelector({
  brand,
  brandConfig,
  brands,
  region,
  regions,
  regionConfig,
  theme,
  darkMode,
  displayName,
  handleBrandChange,
  handleRegionChange,
  canSwitchBrand = true,
  canSwitchRegion = true,
}) {
  return (
    <div className="px-3 py-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors mb-2"
            style={{
              backgroundColor: darkMode ? "#21262d" : "#f1f5f9",
              color: theme.text,
            }}
            data-testid="brand-selector"
          >
            <div className="flex items-center gap-2">
              {brandIcon(brand, theme.primary)}
              <span className="font-medium">{displayName}</span>
            </div>
            {(canSwitchBrand || canSwitchRegion) && (
              <ChevronDown className="w-4 h-4 opacity-50" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-56"
          style={{
            backgroundColor: darkMode ? "#161b22" : "#ffffff",
            borderColor: theme.border,
          }}
        >
          {canSwitchBrand && (
            <>
              <DropdownMenuLabel style={{ color: theme.textMuted }}>Switch Brand</DropdownMenuLabel>
              <DropdownMenuSeparator style={{ backgroundColor: theme.border }} />
              {Object.entries(brands).map(([key, cfg]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => handleBrandChange(key)}
                  className="flex items-center gap-2 cursor-pointer"
                  style={{ color: theme.text }}
                >
                  {brandIcon(key, cfg.theme.primary)}
                  <div className="flex flex-col">
                    <span
                      className={brand === key ? "font-medium" : ""}
                      style={{ color: brand === key ? cfg.theme.primary : theme.text }}
                    >
                      {cfg.fullName}
                    </span>
                    <span className="text-xs" style={{ color: theme.textMuted }}>
                      {cfg.industry}
                    </span>
                  </div>
                  {brand === key && (
                    <span className="ml-auto" style={{ color: cfg.theme.primary }}>
                      ✓
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator style={{ backgroundColor: theme.border }} />
            </>
          )}
          {canSwitchRegion ? (
            <>
              <DropdownMenuLabel style={{ color: theme.textMuted }}>Select Region</DropdownMenuLabel>
              {regions.map((r) => {
                const info = regionConfig[r] || REGION_FALLBACK;
                return (
                  <DropdownMenuItem
                    key={r}
                    onClick={() => handleRegionChange(r)}
                    className="flex items-center gap-2 cursor-pointer"
                    style={{ color: theme.text }}
                  >
                    <span className="text-lg">{info.flag}</span>
                    <span
                      style={{
                        color: region === r ? theme.primary : theme.text,
                        fontWeight: region === r ? 500 : 400,
                      }}
                    >
                      {r}
                    </span>
                    {region === r && (
                      <span className="ml-auto" style={{ color: theme.primary }}>
                        ✓
                      </span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          ) : (
            <DropdownMenuLabel style={{ color: theme.textMuted, fontSize: "0.7rem" }}>
              🔒 Account locked to {region}
            </DropdownMenuLabel>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
